import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo from "@/components/feature/Seo";
import MediaGrid from "@/components/feature/MediaGrid";
import Pagination from "@/components/feature/Pagination";
import FilterPanel, { defaultFilters, type FilterState } from "@/pages/category/components/FilterPanel";
import { genreOptions, mediaList, typeLabel, type MediaType } from "@/mocks/media";

const PAGE_SIZE = 12;

const categoryCopy: Record<MediaType, { title: string; intro: string; icon: string }> = {
  movie: {
    title: "电影频道",
    intro: "电影频道，汇集最新高清电影资源，悬疑、动作、爱情、科幻多种题材，随时挑一部进入故事。",
    icon: "ri-film-line",
  },
  tv: {
    title: "电视剧频道",
    intro: "电视剧频道，热播剧集持续更新，都市、悬疑、家庭、青春应有尽有，追剧不用等。",
    icon: "ri-tv-2-line",
  },
  variety: {
    title: "综艺频道",
    intro: "综艺频道，音乐、美食、旅行、竞技类综艺一网打尽，轻松解压的好选择。",
    icon: "ri-mic-line",
  },
};

type SortKey = "updated" | "views" | "rating" | "title";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "updated", label: "更新时间" },
  { key: "views", label: "点击量" },
  { key: "rating", label: "评分" },
  { key: "title", label: "名称" },
];

const isMediaType = (value?: string): value is MediaType =>
  value === "movie" || value === "tv" || value === "variety";

export default function Category() {
  const { type } = useParams();
  const currentType: MediaType = isMediaType(type) ? type : "movie";
  const copy = categoryCopy[currentType];
  const genres = genreOptions[currentType];

  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [sortKey, setSortKey] = useState<SortKey>("updated");
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);

  const baseList = useMemo(
    () => mediaList.filter((item) => item.type === currentType),
    [currentType]
  );

  const filtered = useMemo(() => {
    const list = baseList.filter((item) => {
      const genreOk = filters.genre === "全部" || item.genres.includes(filters.genre);
      const regionOk = filters.region === "全部" || item.region === filters.region;
      const yearOk = filters.year === "全部" || String(item.year) === filters.year;
      return genreOk && regionOk && yearOk;
    });
    const sorted = [...list].sort((a, b) => {
      if (sortKey === "views") return b.views - a.views;
      if (sortKey === "rating") return b.rating - a.rating;
      if (sortKey === "title") return a.title.localeCompare(b.title, "zh-Hans-CN");
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
    return sorted;
  }, [baseList, filters, sortKey]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const updateFilters = (next: FilterState) => {
    setFilters(next);
    setPage(1);
  };

  const reset = () => {
    setFilters(defaultFilters);
    setPage(1);
  };

  const changeType = () => {
    setFilters(defaultFilters);
    setSortKey("updated");
    setPage(1);
  };

  return (
    <SiteLayout active={currentType}>
      <Seo
        title={`${copy.title} - 91视频网站最新影视资源`}
        description={`${copy.intro} 支持按类型、地区、年份筛选与排序，共收录 ${baseList.length} 部示例作品。`}
        keywords={`${typeLabel[currentType]},91视频网站,${typeLabel[currentType]}在线观看,最新${typeLabel[currentType]},高清${typeLabel[currentType]}`}
      />

      <div className="w-full bg-primary-900">
        <div className="w-full px-4 md:px-6 xl:px-10 py-7 md:py-9">
          <nav aria-label="面包屑" className="flex items-center flex-wrap gap-1.5 text-xs text-background-300">
            <Link to="/" className="hover:text-secondary-300 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span>分类</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-secondary-400">{typeLabel[currentType]}</span>
          </nav>
          <div className="mt-3 flex items-end justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50 flex items-center gap-2.5">
                <span className="w-10 h-10 rounded-md bg-accent-500 text-background-50 flex items-center justify-center shrink-0">
                  <i className={`${copy.icon} text-xl`}></i>
                </span>
                {copy.title}
              </h1>
              <p className="mt-2.5 text-sm text-background-300 max-w-2xl leading-6">{copy.intro}</p>
            </div>
            <div className="hidden md:flex items-center gap-1.5 shrink-0">
              {(["movie", "tv", "variety"] as MediaType[]).map((t) => (
                <Link
                  key={t}
                  to={`/category/${t}`}
                  onClick={changeType}
                  className={`px-3.5 h-9 inline-flex items-center rounded-md text-sm whitespace-nowrap transition-colors ${
                    t === currentType
                      ? "bg-secondary-500 text-foreground-950 font-medium"
                      : "border border-primary-700 text-background-200 hover:border-secondary-400 hover:text-secondary-300"
                  }`}
                >
                  {typeLabel[t]}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 xl:px-10 py-6 md:py-8">
        <div className="hidden md:block">
          <FilterPanel genreOptions={genres} filters={filters} onChange={updateFilters} onReset={reset} />
        </div>

        <div className="flex items-center justify-between gap-3 mt-4 mb-4 flex-wrap">
          <p className="text-sm text-foreground-600">
            共 <span className="text-primary-600 font-medium">{filtered.length}</span> 部作品
            {filtered.length > 0 && (
              <span className="text-foreground-400 text-xs ml-2">
                第 {currentPage} / {totalPages} 页
              </span>
            )}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPanelOpen(true)}
              className="md:hidden h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-background-300 text-sm text-foreground-700 whitespace-nowrap"
            >
              <i className="ri-filter-3-line"></i>
              筛选
            </button>
            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 overflow-x-auto scrollbar-hide">
              {sortOptions.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => {
                    setSortKey(item.key);
                    setPage(1);
                  }}
                  className={`px-3 h-7 rounded-full text-[13px] whitespace-nowrap transition-colors ${
                    sortKey === item.key
                      ? "bg-primary-500 text-background-50 font-medium"
                      : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <MediaGrid items={visible} onReset={reset} />

        {visible.length > 0 && (
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            onChange={(next) => {
              setPage(next);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        )}
      </div>

      {panelOpen && (
        <FilterPanel
          mobile
          genreOptions={genres}
          filters={filters}
          onChange={updateFilters}
          onReset={reset}
          onClose={() => setPanelOpen(false)}
        />
      )}
    </SiteLayout>
  );
}